// SPDX-License-Identifier: Apache-2.0

import React, { useContext, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import AssetDropdown from "./AssetDropDown";
import EthereumAsset from "../../api/types/ethereumAsset";
import StellarAsset from "../../api/types/stellarAsset";
import { NervosAsset } from "../../api/types/nervosAsset";
import { LPPolicy } from "../../api/types/liquidity";
import {
  Hubs,
  hubFeeBps,
  lpPolicyFlags,
  lpSafePriceMinX64,
  lpSafePriceMaxX64,
} from "../../constants/Constants";
import {
  CKB_DECIMALS,
  ETH_DECIMALS,
  lpFromHuman,
  MIN_CKB_DEPOSIT_SHANNONS,
} from "../../constants/lpUnits";
import { mapLPError } from "./lpErrors";

type AnyAsset = EthereumAsset | StellarAsset | NervosAsset;

// Deposits CKB or ETH liquidity. Assets are fetched from the backend so they
// marshal exactly as it expects; the LP owner signs with their own wallet.
function LPDepositForm() {
  const { t } = useTranslation();
  const ctx = useContext(AppContext);

  const [assets, setAssets] = useState<Map<string, AnyAsset>>(new Map());
  const [selected, setSelected] = useState<string>();
  const [amount, setAmount] = useState("");
  // Entered as a percentage; the wire format is basis points (0.1% = 10 bps).
  // Defaults to the hub's own fee: the hub only draws on cells whose fee is at
  // most what it charges, so anything higher would leave the CKB unused.
  const [feeRatePct, setFeeRatePct] = useState(String(hubFeeBps / 100));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    const map = new Map<string, AnyAsset>();
    ctx.client
      .getAssets({ type: "all" })
      .then((resp) => {
        // The ETH↔CKB pool only takes native ETH and native CKB — exclude
        // tokens like PRN (ERC20) and SUDTs.
        if (ctx.client.ethAddress && resp.ethereumAssets) {
          resp.ethereumAssets
            .filter((a) => a.type === "ETH")
            .forEach((a) => map.set(a.assetID(), a));
        }
        if (ctx.client.nervosAddress && resp.nervosAssets) {
          resp.nervosAssets
            .filter((a) => a.type === "CKB")
            .forEach((a) => map.set(a.assetID(), a));
        }
        setAssets(map);
      })
      .catch((err) => setError(mapLPError(err)));
  }, [ctx.client]);

  const selectedAsset = selected ? assets.get(selected) : undefined;
  const isNervos = selectedAsset instanceof NervosAsset;

  const submit = async () => {
    setMessage(undefined);
    setError(undefined);
    if (!selectedAsset) {
      return;
    }
    const hub = Hubs.get(selectedAsset.chainID)?.address ?? "";
    const channelAsset = selectedAsset.toChannelAsset();
    setBusy(true);
    try {
      if (selectedAsset instanceof NervosAsset) {
        const shannons = lpFromHuman(amount, CKB_DECIMALS);
        if (BigInt(shannons) < MIN_CKB_DEPOSIT_SHANNONS) {
          setError(t("lpMinCkbDeposit"));
          return;
        }
        // Creating the cell writes this ETH address into its on-chain data,
        // where it is frozen for the cell's life: it is who receives ETH-pool
        // shares when this cell's traded CKB is later converted. There is no
        // way to set or correct it afterwards, so refuse to create a cell
        // whose owner has no ETH wallet connected rather than strand its
        // future conversions.
        const ethBeneficiary = ctx.client.ethAddress?.toString();
        if (!ethBeneficiary) {
          setError(t("lpBeneficiaryRequired"));
          return;
        }
        const feeRateBps = Math.round(Number(feeRatePct) * 100);
        if (!Number.isFinite(feeRateBps) || feeRateBps < 0 || feeRateBps > 10000) {
          setError(t("lpInvalidFeeRate"));
          return;
        }
        if (feeRateBps > hubFeeBps) {
          setError(t("lpFeeAboveHub", { hubFee: hubFeeBps / 100 }));
          return;
        }
        // The protection is frozen into the cell at creation, so refuse to
        // create one without it rather than create an unprotected cell.
        if (!lpSafePriceMinX64 || !lpSafePriceMaxX64) {
          setError(t("lpNoPriceBand"));
          return;
        }
        const policy = new LPPolicy(
          "0",
          feeRateBps,
          lpPolicyFlags,
          1,
          lpSafePriceMinX64,
          lpSafePriceMaxX64,
        );
        // The backend wants the owner's full CKB (bech32m) address, not the
        // omnilock internal/eth address.
        const ownerAddress =
          await ctx.client.ckbProvider!.getRecommendedAddress();
        const resp = await ctx.client.lpDepositCKB(
          channelAsset,
          ownerAddress,
          shannons,
          hub,
          policy,
          ethBeneficiary,
        );
        setMessage(t("lpDepositSuccess", { tx: resp.txHash }));
      } else {
        const wei = lpFromHuman(amount, ETH_DECIMALS);
        if (BigInt(wei) <= BigInt(0)) {
          setError(t("lpInvalidAmount"));
          return;
        }
        await ctx.client.lpDepositETH(channelAsset, wei, hub);
        setMessage(t("lpDepositSuccess", { tx: "confirmed" }));
      }
    } catch (err) {
      setError(mapLPError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Form
      className="w-100"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Form.Group className="mb-3">
        <Form.Label>{t("lpAsset")}</Form.Label>
        <AssetDropdown
          assets={assets}
          selectedAsset={selected}
          onSelectAsset={(id) => id && setSelected(id)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{t("lpAmount")}</Form.Label>
        <Form.Control
          type="text"
          inputMode="decimal"
          value={amount}
          placeholder="0.0"
          onChange={(e) => setAmount(e.currentTarget.value)}
        />
        {isNervos && (
          <Form.Text className="text-muted">{t("lpDepositRentNote")}</Form.Text>
        )}
      </Form.Group>

      {isNervos && (
        <>
          <Form.Group className="mb-3">
            <Form.Label>{t("lpFeeRatePct")}</Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={feeRatePct}
              onChange={(e) => setFeeRatePct(e.currentTarget.value)}
            />
            <Form.Text className="text-muted">{t("lpFeeRateHelp")}</Form.Text>
          </Form.Group>
          {ctx.client.ethAddress && (
            <p className="text-muted" style={{ fontSize: "0.875em" }}>
              {t("lpBeneficiaryNote", {
                address: ctx.client.ethAddress.toString(),
              })}
            </p>
          )}
        </>
      )}

      {message && <p style={{ color: "#00C9B8" }}>{message}</p>}
      {error && (
        <span className="invalid-feedback" style={{ display: "block" }}>
          {error}
        </span>
      )}

      <Button type="submit" disabled={busy || !selected || !amount}>
        {busy ? t("lpLoading") : t("lpDepositSubmit")}
      </Button>
    </Form>
  );
}

export default LPDepositForm;
